import { useState } from 'react';

/**
 * 30-day signups — one series, one hue (brand), so no legend: the card title
 * names it.  Bars are anchored to the baseline with 4px rounded tops and a 2px
 * gap; hover / focus shows the exact day + count; a visually hidden table is
 * the screen-reader view.
 */
export function SignupsChart({ days }: { days: Array<{ date: string; signups: number }> }) {
  const [active, setActive] = useState<number | null>(null);
  const max = Math.max(1, ...days.map((d) => d.signups));
  const shown = active !== null ? days[active] : undefined;
  const label = (iso: string) =>
    new Date(`${iso}T00:00:00Z`).toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' });

  return (
    <figure className="m-0">
      <div className="mb-1 flex h-5 items-baseline justify-between text-xs text-neutral-400">
        <span>{shown ? `${label(shown.date)} — ${shown.signups} signup${shown.signups === 1 ? '' : 's'}` : 'Hover a bar for the day'}</span>
        <span className="text-neutral-500">max {max}/day</span>
      </div>
      <div
        className="relative flex h-36 items-end gap-[2px] border-b border-neutral-700"
        onMouseLeave={() => setActive(null)}
        aria-hidden
      >
        {/* recessive mid gridline */}
        <div className="pointer-events-none absolute inset-x-0 top-1/2 border-t border-dashed border-neutral-800" />
        {days.map((d, i) => (
          <div
            key={d.date}
            className="group relative flex h-full flex-1 items-end"
            onMouseEnter={() => setActive(i)}
          >
            <div
              className={
                'w-full rounded-t-[4px] transition-colors ' +
                (active === i ? 'bg-orange-400' : d.signups > 0 ? 'bg-orange-500/80' : 'bg-neutral-800')
              }
              style={{ height: d.signups > 0 ? `${Math.max(3, (d.signups / max) * 100)}%` : '2px' }}
            />
          </div>
        ))}
      </div>
      <div className="mt-1 flex justify-between text-[11px] text-neutral-500" aria-hidden>
        <span>{days[0] ? label(days[0].date) : ''}</span>
        <span>{days[Math.floor(days.length / 2)] ? label(days[Math.floor(days.length / 2)]!.date) : ''}</span>
        <span>{days.at(-1) ? `${label(days.at(-1)!.date)} (UTC)` : ''}</span>
      </div>
      <table className="sr-only">
        <caption>Signups per day, last 30 days (UTC)</caption>
        <thead>
          <tr>
            <th>Day</th>
            <th>Signups</th>
          </tr>
        </thead>
        <tbody>
          {days.map((d) => (
            <tr key={d.date}>
              <td>{d.date}</td>
              <td>{d.signups}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </figure>
  );
}
